/**
 * Bulk Sale（批量购买）页面脚本
 *
 * 本文件提供 3 个自定义元素：
 * - <bulk-sale-page>：页面容器，负责汇总选中数量、计算阶梯折扣、加载更多/无限滚动、批量加购/直达结账、粘性栏明细。
 * - <bulk-sale-product-card>：单个商品卡片，负责变体切换、数量增减，并按变体记忆数量。
 * - <tiered-discount-progress>：阶梯折扣进度条（桌面/移动两种布局）。
 *
 * 依赖（可选）：
 * - window.theme.Currency.formatMoney：用于金额格式化；缺失时回退为 $xx.xx。
 * - window.theme.routes.cart_add_url / root_url：用于加购与直达结账；缺失时回退为 /cart/add、/。
 *
 * 集成要求（按功能拆分，缺失会降级或不生效）：
 *
 * 1) <bulk-sale-page>（必须）
 * - 商品网格：#product-grid
 * - 商品卡片：带 data-card 的 <bulk-sale-product-card>（或任意元素，但需实现本文件约定的方法；推荐直接使用该自定义元素）
 * - 汇总/按钮：data-total-items、data-total-price、data-add-to-cart、data-buy-now
 * - 粘性容器：data-sticky（脚本会根据是否选中切换 body class）
 *
 * 2) 折扣配置（写在 <bulk-sale-page> 上，可选）
 * - data-discount-basis="quantity"（默认）或 "amount"
 * - 推荐使用 data-discount-tiers 传 JSON（percent 为整数）：
 *   [{"qty":5,"percent":15},{"qty":10,"percent":20},{"qty":20,"percent":25}]
 *   金额阶梯（单位：分）：
 *   [{"amountCents":5000,"percent":10},{"amountCents":10000,"percent":15}]
 * - 折扣规则统一来自 campaignId 对应的 data-discount-tiers
 *
 * 3) 进度条与文案（可选）
 * - 文案节点：data-discount-message-desktop / data-discount-message-mobile
 * - 进度条：<tiered-discount-progress data-tier-progress="desktop|mobile"></tiered-discount-progress>
 *
 * 4) 商品卡片结构（<bulk-sale-product-card> 内，必须）
 * - 变体选择：<select data-variant-select>，option.value = variantId
 *   option 需提供：
 *   - data-variant-price-cents（单位：分）
 *   - data-variant-qty-min、data-variant-qty-max（0 表示不限制，内部会当作 999）
 *   可选：
 *   - data-variant-image-src / data-variant-image-srcset / data-variant-image-alt
 *   - data-variant-url
 * - 数量控件：
 *   - [data-qty] 容器（脚本会写入 data-qty）
 *   - [data-qty-minus] / [data-qty-plus] 按钮
 *   - [data-qty-value]（input 或普通元素；input 会自动约束 min/max）
 *
 * 5) 筛选（可选）
 * - 下拉：.bulk-sale-filter__select[data-filter-url-select]
 * - option 需提供 data-filter-url，选中后会跳转到该 URL，并清除 ?page
 *
 * 6) 分页（可选）
 * - pagination-theme.pagination
 *   - .pagination-type--loadmore（按钮 .load-more）或 .pagination-type--infinite（进入视口自动加载）
 *   - data-section（当前 section id）
 * - #product-grid[data-id="<sectionId>"]：用于从下一页 HTML 中定位并追加卡片
 *
 * 事件
 * - <bulk-sale-product-card> 会冒泡触发 "bulk-sale-card:change"，<bulk-sale-page> 监听后刷新汇总与折扣展示。
 */

const {
  parseInteger: toInt,
  parseDecimal: toFloat,
  parseJson,
  normalizeBasis,
  normalizeCampaigns,
  parseDiscountTiers,
  parseDiscountCampaigns,
  getTierThreshold,
  getCurrentTier,
  getNextTier,
  getExtendedProgressMax,
  optimizeDiscountAssignments,
  buildCampaignResult,
  buildCampaignResultsFromSolution,
  selectBestCampaignResult,
} = window.ElegooTieredDiscountUtils || {};

const bulkSaleHtmlEntityDecoder = document.createElement("textarea");
function decodeHtmlEntities(value) {
  const input = String(value ?? "");
  if (!input || !input.includes("&")) return input;
  bulkSaleHtmlEntityDecoder.innerHTML = input;
  return bulkSaleHtmlEntityDecoder.value;
}

function formatMoneyTiered(cents) {
  let format =
    window.theme.settings.money_with_currency_format || "${{amount}}";
  return formatMoney(cents, format);
}

function parseCampaignIds(raw) {
  const value = String(raw || "").trim();
  if (!value) return [];
  const parsed = parseJson(value, []);
  if (!Array.isArray(parsed)) {
    return [];
  }
  return parsed.map((s) => String(s || "").trim()).filter(Boolean);
}

/**
 * TieredDiscountProgress
 * @class
 * @description A custom element for rendering a tiered discount progress bar.
 */
if (!customElements.get("tiered-discount-progress")) {
  class TieredDiscountProgress extends HTMLElement {
    constructor() {
      super();
      this.variant = "desktop";
      this.fillEl = null;
      this.dots = [];
      this.initialized = false;
      this.handlePointClick = this.handlePointClick.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.variant = this.getAttribute("data-tier-progress") || "desktop";
      this.style.display = "block";
      if (this.variant === "mobile") this.style.height = "100%";
      this.renderBase();
      this.addEventListener("click", this.handlePointClick);
      document.addEventListener("click", this.handleDocumentClick);
    }

    renderBase() {
      if (this.variant === "mobile") {
        this.innerHTML = `<div class="bulk-sale-sticky__mobile-fill"></div>`;
        this.fillEl = this.querySelector(".bulk-sale-sticky__mobile-fill");
        return;
      }
      this.innerHTML = `<div class="bulk-sale-sticky__progress-fill"></div>`;
      this.fillEl = this.querySelector(".bulk-sale-sticky__progress-fill");
    }

    ensureDots(count) {
      const wanted = Math.max(0, toInt(count, 0));
      if (this.dots.length === wanted) return;
      this.dots.forEach((d) => d.remove());
      this.dots = [];

      for (let i = 0; i < wanted; i += 1) {
        const dot = document.createElement("span");
        dot.className =
          this.variant === "mobile"
            ? "bulk-sale-sticky__mobile-dot"
            : "bulk-sale-sticky__progress-dot";
        dot.tabIndex = 0;
        dot.setAttribute("role", "button");
        dot.setAttribute("aria-expanded", "false");
        dot.dataset.tierProgressPoint = "";
        dot.innerHTML =
          '<span class="bulk-sale-sticky__progress-tooltip"></span>';
        this.appendChild(dot);
        this.dots.push(dot);
      }
    }

    isMobileViewport() {
      return window.matchMedia("(max-width: 767px)").matches;
    }

    closeTooltips() {
      this.dots.forEach((dot) => {
        dot.classList.remove("is-tooltip-open");
        dot.setAttribute("aria-expanded", "false");
      });
    }

    toggleTooltip(dot) {
      const willOpen = !dot.classList.contains("is-tooltip-open");
      this.closeTooltips();
      if (willOpen) {
        dot.classList.add("is-tooltip-open");
        dot.setAttribute("aria-expanded", "true");
      }
    }

    handlePointClick(event) {
      if (!this.isMobileViewport()) return;
      const dot = event.target.closest("[data-tier-progress-point]");
      if (!dot || !this.contains(dot)) return;
      event.stopPropagation();
      this.toggleTooltip(dot);
    }

    handleDocumentClick(event) {
      if (!this.isMobileViewport()) return;
      if (this.contains(event.target)) return;
      this.closeTooltips();
    }

    update({ value, tiers, offLabel }) {
      const safeTiers = Array.isArray(tiers) ? tiers : [];
      const metric = Number.isFinite(value) ? value : 0;
      const basis = safeTiers[0]?.basis || "quantity";
      const max = getExtendedProgressMax(safeTiers, basis);
      const safeOffLabel = offLabel || "off";

      this.ensureDots(safeTiers.length);
      this.closeTooltips();

      const ratio = max > 0 ? Math.max(0, Math.min(1, metric / max)) : 0;
      if (this.fillEl) this.fillEl.style.width = `${Math.round(ratio * 100)}%`;

      this.dots.forEach((dot, idx) => {
        const threshold = toInt(safeTiers[idx]?.threshold, 0);
        const pos = max > 0 ? Math.max(0, Math.min(1, threshold / max)) : 0;
        const tooltip = dot.querySelector(
          ".bulk-sale-sticky__progress-tooltip",
        );
        const thresholdLabel =
          safeTiers[idx]?.basis === "amount"
            ? formatMoneyTiered(threshold)
            : `${threshold}+`;
        const valueType =
          String(safeTiers[idx]?.valueType || "").toLowerCase() === "amount"
            ? "amount"
            : "percentage";
        const tooltipValueLabel =
          valueType === "amount"
            ? `${formatMoneyTiered(toInt(safeTiers[idx]?.amountOffCents, 0))} ${safeOffLabel}`
            : `${toInt(safeTiers[idx]?.percent, 0)}%`;
        const tooltipLabel = `${thresholdLabel} · ${tooltipValueLabel}`;
        dot.style.left = `${pos * 100}%`;
        dot.classList.toggle("is-active", metric >= threshold && threshold > 0);
        dot.setAttribute("aria-label", tooltipLabel);
        if (tooltip) {
          tooltip.textContent = tooltipLabel;
        }
      });
    }
  }

  customElements.define("tiered-discount-progress", TieredDiscountProgress);
}

/**
 * BulkSaleProductCard
 * @class
 * @description A custom element for rendering a bulk sale product card.
 *
 * 使用要求（核心节点）
 * - [data-variant-select]：select option 需要携带 data-variant-*（价格/限购/图片/链接）
 * - [data-qty] / [data-qty-value] / [data-qty-minus] / [data-qty-plus]：数量控制
 */
if (!customElements.get("bulk-sale-product-card")) {
  class BulkSaleProductCard extends HTMLElement {
    constructor() {
      super();
      this.variantSelect = null;
      this.imageEl = null;
      this.imageLinkEl = null;
      this.priceContainerEl = null;
      this.qtyRoot = null;
      this.qtyValueEl = null;
      this.qtyMinusBtn = null;
      this.qtyPlusBtn = null;
      this.defaultImage = { src: "", srcset: "", alt: "" };
      this.defaultLink = "";
      this.variantQtyById = new Map();
      this.variantMetaById = new Map();
      this._restoringState = false;
    }

    connectedCallback() {
      if (this.dataset.initialized === "true") return;
      this.dataset.initialized = "true";

      this.variantSelect = this.querySelector("[data-variant-select]");
      this.imageEl = this.querySelector(".bulk-sale-card__image");
      this.imageLinkEl = this.querySelector(".bulk-sale-card__image-link");
      this.priceContainerEl = this.querySelector("[data-price-container]");
      this.qtyRoot = this.querySelector("[data-qty]");
      this.qtyValueEl = this.querySelector("[data-qty-value]");
      this.qtyMinusBtn = this.querySelector("[data-qty-minus]");
      this.qtyPlusBtn = this.querySelector("[data-qty-plus]");

      if (this.qtyRoot && !this.qtyRoot.dataset.qty) {
        this.qtyRoot.dataset.qty = "0";
      }

      if (!this.dataset.qty) {
        this.dataset.qty = this.qtyRoot?.dataset.qty || "0";
      }

      if (this.imageEl) {
        this.defaultImage = {
          src:
            this.imageEl.dataset.defaultSrc ||
            this.imageEl.getAttribute("src") ||
            "",
          srcset:
            this.imageEl.dataset.defaultSrcset ||
            this.imageEl.getAttribute("srcset") ||
            "",
          alt:
            this.imageEl.dataset.defaultAlt ||
            this.imageEl.getAttribute("alt") ||
            "",
        };
      }
      if (this.imageLinkEl) {
        this.defaultLink =
          this.imageLinkEl.dataset.defaultHref ||
          this.imageLinkEl.getAttribute("href") ||
          "";
      }

      if (this.variantSelect) {
        this.variantSelect.addEventListener("change", () =>
          this.onVariantChange(),
        );
      }

      if (this.qtyMinusBtn && this.qtyValueEl) {
        this.qtyMinusBtn.addEventListener("click", () => {
          const current = this.getQty();
          const min = this.getVariantQtyMin();
          if (current <= 0) return;
          let next = current - 1;
          if (next > 0 && next < min) next = min;
          this.setQty(next);
          this.syncCurrentVariantQty();
          this.emitChange();
        });
      }

      if (this.qtyPlusBtn && this.qtyValueEl) {
        this.qtyPlusBtn.addEventListener("click", () => {
          const current = this.getQty();
          const min = this.getVariantQtyMin();
          const max = this.getVariantQtyMax();
          if (current >= max) return;
          const next =
            current <= 0 ? Math.max(1, min) : Math.min(max, current + 1);
          this.setQty(next);
          this.syncCurrentVariantQty();
          this.emitChange();
        });
      }

      if (this.qtyValueEl instanceof HTMLInputElement) {
        const syncFromInput = () => {
          const raw = this.qtyValueEl.value;
          if (raw === "") {
            this.setQty(0);
          } else {
            const parsed = parseInt(raw, 10);
            if (Number.isNaN(parsed)) return;
            const min = this.getVariantQtyMin();
            const max = this.getVariantQtyMax();
            const next = parsed <= 0 ? 0 : Math.min(max, Math.max(min, parsed));
            this.setQty(next);
          }
          this.syncCurrentVariantQty();
          this.emitChange();
        };

        this.qtyValueEl.addEventListener("change", syncFromInput);
        this.qtyValueEl.addEventListener("blur", syncFromInput);
        this.qtyValueEl.addEventListener("keydown", (e) => {
          if (e.key !== "Enter") return;
          e.preventDefault();
          syncFromInput();
          this.qtyValueEl.blur();
        });
      }

      this.syncFromVariantSelect();
      this.variantQtyById = this.readVariantQtyById();
      this.variantMetaById = this.readVariantMetaById();
      this.syncCurrentVariantQty({ preferStored: true });
      this.emitChange();
    }

    getQty() {
      return toInt(this.dataset.qty, 0) || 0;
    }

    getVariantQtyMin() {
      return toInt(this.dataset.variantQtyMin, 0) || 0;
    }

    getVariantQtyMax() {
      const raw = toInt(this.dataset.variantQtyMax, 0) || 0;
      return raw > 0 ? raw : 999;
    }

    updateQtyButtons() {
      const qty = this.getQty();
      const min = this.getVariantQtyMin();
      const max = this.getVariantQtyMax();

      if (this.qtyMinusBtn) {
        this.qtyMinusBtn.disabled = qty <= 0 || (qty > 0 && qty <= min);
      }
      if (this.qtyPlusBtn) {
        this.qtyPlusBtn.disabled = qty >= max;
      }
    }

    setQty(nextQty) {
      const min = this.getVariantQtyMin();
      const max = this.getVariantQtyMax();
      let safe = Math.max(0, Math.min(max, nextQty));
      if (safe > 0 && safe < min) safe = min;
      this.dataset.qty = String(safe);
      if (this.qtyRoot) this.qtyRoot.dataset.qty = String(safe);
      if (!this.qtyValueEl) {
        console.warn(
          "[BulkSaleProductCard] qtyValueEl not found, UI sync skipped",
        );
        this.updateQtyButtons();
        return;
      }
      if (this.qtyValueEl instanceof HTMLInputElement) {
        const val = String(safe);
        this.qtyValueEl.value = val;
        this.qtyValueEl.setAttribute("value", val);
      } else {
        this.qtyValueEl.textContent = String(safe);
      }
      this.updateQtyButtons();
    }

    getVariantId() {
      return toInt(this.dataset.variantId, 0) || 0;
    }

    getVariantPriceCents() {
      return toInt(this.dataset.variantPriceCents, 0) || 0;
    }

    getVariantCompareAtPriceCents() {
      return toInt(this.dataset.variantCompareAtPriceCents, 0) || 0;
    }

    readVariantQtyById() {
      const raw = this.dataset.variantQtyById;
      if (!raw) return new Map();
      try {
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") return new Map();
        const map = new Map();
        Object.entries(parsed).forEach(([k, v]) => {
          const variantId = toInt(k, 0) || 0;
          const qty = toInt(v, 0) || 0;
          if (variantId > 0 && qty > 0) map.set(variantId, qty);
        });
        return map;
      } catch {
        return new Map();
      }
    }

    readVariantMetaById() {
      const map = new Map();
      if (!this.variantSelect) return map;
      Array.from(this.variantSelect.options || []).forEach((opt) => {
        const variantId = toInt(opt.value, 0) || 0;
        if (!variantId) return;
        map.set(variantId, {
          priceCents: toInt(opt.dataset?.variantPriceCents, 0) || 0,
          compareAtPriceCents:
            toInt(opt.dataset?.variantCompareAtPriceCents, 0) || 0,
          campaignId: opt.dataset?.variantCampaignId || "",
          qtyMin: toInt(opt.dataset?.variantQtyMin, 0) || 0,
          qtyMax: toInt(opt.dataset?.variantQtyMax, 0) || 0,
          imageSrc: opt.dataset?.variantImageSrc || "",
          imageSrcset: opt.dataset?.variantImageSrcset || "",
          imageAlt: opt.dataset?.variantImageAlt || "",
          url: opt.dataset?.variantUrl || "",
        });
      });
      return map;
    }

    getVariantMetaById(variantId) {
      const id = toInt(variantId, 0) || 0;
      return id ? this.variantMetaById.get(id) : null;
    }

    writeVariantQtyById() {
      const obj = {};
      this.variantQtyById.forEach((qty, variantId) => {
        if (variantId > 0 && qty > 0) obj[String(variantId)] = qty;
      });
      this.dataset.variantQtyById = JSON.stringify(obj);
    }

    syncCurrentVariantQty({ preferStored } = { preferStored: false }) {
      const variantId = this.getVariantId();
      if (!variantId) return;

      if (preferStored) {
        const stored = this.variantQtyById.get(variantId);
        if (typeof stored === "number") {
          this.setQty(stored);
        } else {
          this.variantQtyById.set(variantId, this.getQty());
        }
      } else {
        this.variantQtyById.set(variantId, this.getQty());
      }

      this.writeVariantQtyById();
    }

    onVariantChange() {
      if (this._restoringState) {
        this.syncFromVariantSelect();
        const nextVariantId = this.getVariantId();
        const nextQty = nextVariantId
          ? this.variantQtyById.get(nextVariantId)
          : 0;
        this.setQty(typeof nextQty === "number" ? nextQty : 0);
        this.syncCurrentVariantQty();
        return;
      }

      const prevVariantId = this.getVariantId();
      if (prevVariantId) {
        this.variantQtyById.set(prevVariantId, this.getQty());
      }

      this.syncFromVariantSelect();

      const nextVariantId = this.getVariantId();
      const nextQty = nextVariantId
        ? this.variantQtyById.get(nextVariantId)
        : 0;
      this.setQty(typeof nextQty === "number" ? nextQty : 0);
      this.syncCurrentVariantQty();
      this.emitChange();
    }

    getVariantPriceCentsById(variantId) {
      const meta = this.getVariantMetaById(variantId);
      return meta ? meta.priceCents : 0;
    }

    getVariantCompareAtPriceCentsById(variantId) {
      const meta = this.getVariantMetaById(variantId);
      return meta ? meta.compareAtPriceCents : 0;
    }

    getVariantOriginalPriceCentsById(variantId) {
      const priceCents = this.getVariantPriceCentsById(variantId);
      const compareAtPriceCents =
        this.getVariantCompareAtPriceCentsById(variantId);
      return compareAtPriceCents > priceCents
        ? compareAtPriceCents
        : priceCents;
    }

    getVariantCampaignIdById(variantId) {
      const meta = this.getVariantMetaById(variantId);
      return meta ? String(meta.campaignId || "").trim() : "";
    }

    getCurrentCampaignId() {
      return (
        this.dataset.variantCampaignId ||
        this.getVariantCampaignIdById(this.getVariantId()) ||
        this.dataset.productCampaignId ||
        ""
      ).trim();
    }

    getVariantRulesById(variantId) {
      const meta = this.getVariantMetaById(variantId);
      const min = meta ? meta.qtyMin : 0;
      const rawMax = meta ? meta.qtyMax : 0;
      const max = rawMax > 0 ? rawMax : 999;
      return { min, max };
    }

    getAllSelectedItems() {
      return this.getAllSelectedSummary().items;
    }

    getAllSelectedItemsCount() {
      return this.getAllSelectedSummary().count;
    }

    getAllSelectedSubtotalCents() {
      return this.getAllSelectedSummary().subtotalCents;
    }

    getAllSelectedSummary() {
      this.syncCurrentVariantQty();
      const items = [];
      let count = 0;
      let subtotalCents = 0;
      this.variantQtyById.forEach((qty, variantId) => {
        if (!variantId || qty <= 0) return;
        const priceCents = this.getVariantPriceCentsById(variantId);
        const originalPriceCents =
          this.getVariantOriginalPriceCentsById(variantId);
        items.push({
          id: variantId,
          quantity: qty,
          priceCents,
          originalPriceCents,
          campaignId:
            this.getVariantCampaignIdById(variantId) ||
            String(this.dataset.productCampaignId || "").trim(),
        });
        count += qty;
        subtotalCents += priceCents * qty;
      });
      return { items, count, subtotalCents };
    }

    setVariantQty(variantId, qty) {
      const id = parseInt(String(variantId || "0"), 10) || 0;
      if (!id) return;
      const { min, max } = this.getVariantRulesById(id);
      const parsedQty = parseInt(String(qty || "0"), 10) || 0;
      let safe = Math.max(0, Math.min(max, parsedQty));
      if (safe > 0 && safe < min) safe = min;
      this.variantQtyById.set(id, safe);
      if (id === this.getVariantId()) this.setQty(safe);
      this.writeVariantQtyById();
      this.emitChange();
    }

    syncFromVariantSelect() {
      if (!this.variantSelect) return;
      const option =
        this.variantSelect.options?.[this.variantSelect.selectedIndex];
      const variantId = parseInt(this.variantSelect.value || "0", 10) || 0;
      const priceCents =
        parseInt(option?.dataset?.variantPriceCents || "0", 10) || 0;
      const compareAtPriceCents =
        parseInt(option?.dataset?.variantCompareAtPriceCents || "0", 10) || 0;
      const qtyMin = parseInt(option?.dataset?.variantQtyMin || "0", 10) || 0;
      const qtyMax = parseInt(option?.dataset?.variantQtyMax || "0", 10) || 0;

      this.dataset.variantId = String(variantId);
      this.dataset.variantPriceCents = String(priceCents);
      this.dataset.variantCompareAtPriceCents = String(compareAtPriceCents);
      this.dataset.variantCampaignId =
        option?.dataset?.variantCampaignId?.trim() || "";
      this.dataset.variantQtyMin = String(qtyMin);
      this.dataset.variantQtyMax = String(qtyMax);

      if (this.qtyValueEl instanceof HTMLInputElement) {
        this.qtyValueEl.min = String(qtyMin);
        this.qtyValueEl.max = qtyMax > 0 ? String(qtyMax) : "999";
      }
      this.updateQtyButtons();

      this.syncImageFromVariantOption(option);
      this.syncPriceFromVariantOption(option);
    }

    syncImageFromVariantOption(option) {
      if (!this.imageEl) return;
      const nextSrc = option?.dataset?.variantImageSrc || this.defaultImage.src;
      const nextSrcset =
        option?.dataset?.variantImageSrcset || this.defaultImage.srcset;
      const nextAlt = option?.dataset?.variantImageAlt || this.defaultImage.alt;
      const nextHref = option?.dataset?.variantUrl || this.defaultLink;

      if (nextSrc) this.imageEl.setAttribute("src", nextSrc);
      if (nextSrcset) this.imageEl.setAttribute("srcset", nextSrcset);
      if (typeof nextAlt === "string")
        this.imageEl.setAttribute("alt", nextAlt);
      if (this.imageLinkEl && nextHref)
        this.imageLinkEl.setAttribute("href", nextHref);
    }

    syncPriceFromVariantOption(option) {
      if (!this.priceContainerEl) return;
      const priceCents =
        parseInt(option?.dataset?.variantPriceCents || "0", 10) || 0;
      const compareAtPriceCents =
        parseInt(option?.dataset?.variantCompareAtPriceCents || "0", 10) || 0;

      const priceAmountEl =
        this.priceContainerEl.querySelector(".price ins .amount");
      const comparePriceAmountEl =
        this.priceContainerEl.querySelector(".price del .amount");

      if (priceAmountEl) {
        priceAmountEl.textContent = formatMoneyTiered(priceCents);
      }

      if (comparePriceAmountEl) {
        if (compareAtPriceCents > 0 && compareAtPriceCents !== priceCents) {
          comparePriceAmountEl.textContent =
            formatMoneyTiered(compareAtPriceCents);
          comparePriceAmountEl.parentElement.style.display = "";
        } else {
          comparePriceAmountEl.parentElement.style.display = "none";
        }
      }
    }

    emitChange() {
      this.dispatchEvent(
        new CustomEvent("bulk-sale-card:change", {
          bubbles: true,
          composed: true,
          detail: {
            qty: this.getQty(),
            variantId: this.getVariantId(),
            variantPriceCents: this.getVariantPriceCents(),
          },
        }),
      );
    }

    clearSelection() {
      this.variantQtyById.forEach((qty, variantId) => {
        if (!variantId || qty <= 0) return;
        this.variantQtyById.set(variantId, 0);
      });
      this.writeVariantQtyById();
      this.setQty(0);
      this.emitChange();
    }
  }

  customElements.define("bulk-sale-product-card", BulkSaleProductCard);
}

/**
 * BulkSalePage
 * @class
 * @description A custom element for rendering a bulk sale page.
 *
 * 使用要求（核心节点）
 * - #product-grid：商品网格容器
 * - [data-card]：每个卡片（推荐为 <bulk-sale-product-card>）
 * - data-total-items / data-total-price：汇总展示
 * - data-add-to-cart / data-buy-now：批量操作按钮
 */
if (!customElements.get("bulk-sale-page")) {
  class BulkSalePage extends HTMLElement {
    constructor() {
      super();
      this.cards = Array.from(this.querySelectorAll("[data-card]"));
      this.grid = this.querySelector("#product-grid");
      this.pagination = this.querySelector("pagination-theme.pagination");
      this.filterSelects = [];
      this.stickyEl = this.querySelector("[data-sticky]");
      this.itemsBtn = this.querySelector("[data-items-button]");
      this.detailsEl = this.querySelector("[data-details]");
      this.detailsGridEl = this.querySelector("[data-details-grid]");
      this.subtotalTitleEl = this.querySelector("[data-subtotal-title]");
      this.detailsCloseBtn = this.querySelector("[data-details-close]");
      this.isDetailsOpen = false;
      this.detailsSignature = "";
      this.onDocumentKeydown = null;
      this.totalPriceEl = this.querySelector("[data-total-price]");
      this.totalItemsEl = this.querySelector("[data-total-items]");
      this.discountPillEl = this.querySelector("[data-discount-pill]");
      this.discountAmountEl = this.querySelector("[data-discount-amount]");
      this.desktopMessageEl = this.querySelector(
        "[data-discount-message-desktop]",
      );
      this.mobileMessageEl = this.querySelector(
        "[data-discount-message-mobile]",
      );
      this.desktopProgress = this.querySelector(
        'tiered-discount-progress[data-tier-progress="desktop"]',
      );
      this.mobileProgress = this.querySelector(
        'tiered-discount-progress[data-tier-progress="mobile"]',
      );
      this.atcBtn = this.querySelector("[data-add-to-cart]");
      this.buyNowBtn = this.querySelector("[data-buy-now]");
      this.actionErrorEl = this.querySelector("[data-action-error]");
      this.actionErrorMessageEl = this.querySelector(
        "[data-action-error-message]",
      );
      this.isLoadingNextPage = false;
      this.paginationObserver = null;
      this.discountBasis = this.getDiscountBasis();
      this.discountValueType = this.getDiscountValueType();
      this.tiers = this.getDiscountTiers();
      this.discountCampaigns = this.getDiscountCampaigns();
      this.discountCampaignIds = this.discountCampaigns.map(
        (campaign) => campaign.discountId,
      );
      this.activeCampaignId = (
        this.getAttribute("data-campaign-id") || ""
      ).trim();
      this.lastMetricValue = 0;
      this.itemLabelSingular = decodeHtmlEntities(
        this.getAttribute("data-item-label-singular") || "",
      );
      this.itemLabelPlural = decodeHtmlEntities(
        this.getAttribute("data-item-label-plural") || "",
      );
      this.subtotalLabel = decodeHtmlEntities(
        this.getAttribute("data-subtotal-label") || "",
      );
      this.tooltipOffLabel = decodeHtmlEntities(
        this.getAttribute("data-tooltip-off-label") || "",
      );
      this.messageAddTemplate = decodeHtmlEntities(
        this.getAttribute("data-message-add") || "",
      );
      this.messageUpgradeTemplate = decodeHtmlEntities(
        this.getAttribute("data-message-upgrade") || "",
      );
      this.messageUnlockedTemplate = decodeHtmlEntities(
        this.getAttribute("data-message-unlocked") || "",
      );
      this.removeLabel = decodeHtmlEntities(
        this.getAttribute("data-remove-label") || "",
      );
      this.selectionErrorLabel = decodeHtmlEntities(
        this.getAttribute("data-selection-error") || "",
      );
      this.ghostSelections = [];
    }

    connectedCallback() {
      if (this.dataset.initialized === "true") return;
      this.dataset.initialized = "true";
      this.setupFilters();
      this.setupSelectMirrors();
      this.setupCardChangeBridge();
      this.setupPagination();
      this.setupActions();
      this.setupDetails();
      this.updateTotals();
    }

    disconnectedCallback() {
      document.body.classList.remove("bulk-sale-sticky-active");
      if (this.paginationObserver) {
        this.paginationObserver.disconnect();
        this.paginationObserver = null;
      }
      if (this.onDocumentKeydown) {
        document.removeEventListener("keydown", this.onDocumentKeydown);
        this.onDocumentKeydown = null;
      }
    }

    getDiscountTiers() {
      const basis = this.discountBasis || this.getDiscountBasis();
      const valueType = this.discountValueType || this.getDiscountValueType();
      const tiers =
        parseDiscountTiers(this.getAttribute("data-discount-tiers")) || [];

      const thresholdOf = (t) =>
        basis === "amount" ? toInt(t?.amountCents, 0) : toInt(t?.qty, 0);

      const normalized = tiers
        .map((t) => ({
          qty: toInt(t?.qty, 0),
          amountCents: toInt(t?.amountCents, 0),
          percent: toInt(t?.percent, 0),
          amountOffCents: toInt(t?.amountOffCents, 0),
        }))
        .filter(
          (t) =>
            thresholdOf(t) > 0 &&
            (valueType === "amount" ? t.amountOffCents > 0 : t.percent > 0),
        )
        .sort((a, b) => thresholdOf(a) - thresholdOf(b));

      return normalized;
    }

    getDiscountValueType() {
      const value = String(this.getAttribute("data-discount-value-type") || "")
        .trim()
        .toLowerCase();
      return value === "amount" ? "amount" : "percentage";
    }

    getDiscountCampaigns() {
      const campaigns = parseDiscountCampaigns(
        this.getAttribute("data-discount-campaigns"),
      );
      if (campaigns.length) return campaigns;

      const fallbackTiers = this.getDiscountTiers();
      const fallbackId = (this.getAttribute("data-campaign-id") || "").trim();
      if (!fallbackId || !fallbackTiers.length) return [];

      return [
        {
          discountId: fallbackId,
          discountTitle: "",
          basis: this.getDiscountBasis(),
          valueType: this.getDiscountValueType(),
          tiers: fallbackTiers,
        },
      ];
    }

    getDiscountBasis() {
      const raw = (
        this.getAttribute("data-discount-basis") || ""
      ).toLowerCase();
      return raw === "amount" ? "amount" : "quantity";
    }

    setupFilters() {
      this.filterSelects = Array.from(
        this.querySelectorAll(
          ".bulk-sale-filter__select[data-filter-url-select]",
        ),
      );
      if (!this.filterSelects.length) return;

      this.filterSelects.forEach((select) => {
        if (select.dataset.filterInitialized === "true") return;
        select.dataset.filterInitialized = "true";
        select.addEventListener("change", (event) =>
          this.onFiltersChanged(event),
        );
      });

      if (this.dataset.popstateInitialized !== "true") {
        this.dataset.popstateInitialized = "true";
        window.addEventListener("popstate", (event) => {
          const search =
            event.state?.searchParams || window.location.search.slice(1);
          const nextUrl = new URL(
            window.location.pathname + (search ? "?" + search : ""),
            window.location.origin,
          );
          this.renderPageByUrl(nextUrl, false);
        });
      }
    }

    onFiltersChanged(event) {
      const select = event?.currentTarget;
      if (!(select instanceof HTMLSelectElement)) return;
      const option = select.options?.[select.selectedIndex];
      const rawUrl = option?.dataset?.filterUrl;
      if (!rawUrl) return;
      const nextUrl = new URL(rawUrl, window.location.origin);
      nextUrl.searchParams.delete("page");
      this.renderPageByUrl(nextUrl, true);
    }

    getSectionId() {
      const grid = this.querySelector("#product-grid");
      return grid?.dataset?.id || "";
    }

    serializeCardStates() {
      const cardEntries = [];
      this.cards.forEach((card, idx) => {
        if (typeof card.getAllSelectedSummary !== "function") return;
        const key = this.getCardKey(card);
        const productTitle =
          card.querySelector(".bulk-sale-card__title")?.textContent?.trim() ||
          "";
        const imgEl = card.querySelector(".bulk-sale-card__image");
        const defaultSrc =
          imgEl?.dataset?.defaultSrc || imgEl?.getAttribute("src") || "";
        const defaultAlt =
          imgEl?.dataset?.defaultAlt || imgEl?.getAttribute("alt") || "";
        const select = card.querySelector("[data-variant-select]");
        const options = select ? Array.from(select.options || []) : [];
        const optionByVariantId = new Map();
        options.forEach((opt) => {
          const variantId = parseInt(opt.value || "0", 10) || 0;
          if (variantId) optionByVariantId.set(variantId, opt);
        });

        const s = card.getAllSelectedSummary();
        const items = (s?.items || []).filter(
          (it) => it && it.id && it.quantity > 0,
        );
        if (!items.length) return;
        cardEntries.push({
          key,
          items: items.map((it) => {
            const vid = toInt(it.id, 0) || 0;
            const opt = optionByVariantId.get(vid) || null;
            const variantTitle = opt?.textContent?.trim() || "";
            const imageSrc = opt?.dataset?.variantImageSrc || defaultSrc;
            const imageAlt =
              opt?.dataset?.variantImageAlt || variantTitle || defaultAlt;
            return {
              id: vid,
              quantity: toInt(it.quantity, 0) || 0,
              priceCents: toInt(it.priceCents, 0) || 0,
              originalPriceCents:
                toInt(it.originalPriceCents, 0) > 0
                  ? toInt(it.originalPriceCents, 0)
                  : toInt(it.priceCents, 0) || 0,
              campaignId: String(it.campaignId || "").trim(),
              productTitle,
              variantTitle,
              imageSrc,
              imageAlt,
            };
          }),
        });
      });
      const ghostItems = Array.isArray(this.ghostSelections)
        ? this.ghostSelections.filter((it) => it && it.id && it.quantity > 0)
        : [];
      return { cardEntries, ghostItems };
    }

    _restoreCardVariantQty(card, variantId, qty, cardRestoredVariantIds) {
      const select = card.querySelector("[data-variant-select]");
      if (!select) {
        card.variantQtyById.set(variantId, qty);
        card.writeVariantQtyById();
        cardRestoredVariantIds.add(variantId);
        card.emitChange();
        return;
      }

      const option = Array.from(select.options || []).find(
        (opt) => (parseInt(opt.value || "0", 10) || 0) === variantId,
      );
      if (!option) {
        card.variantQtyById.set(variantId, qty);
        card.writeVariantQtyById();
        cardRestoredVariantIds.add(variantId);
        card.emitChange();
        return;
      }

      const currentVariantId = card.getVariantId();
      if (currentVariantId === variantId) {
        card.setVariantQty(variantId, qty);
        cardRestoredVariantIds.add(variantId);
        return;
      }

      if (currentVariantId && currentVariantId > 0) {
        const currentQty = card.getQty();
        if (currentQty > 0) {
          card.variantQtyById.set(currentVariantId, currentQty);
        }
      }

      card.variantQtyById.set(variantId, qty);
      card._restoringState = true;
      setTimeout(() => {
        select.value = String(variantId);
        select.dispatchEvent(new Event("change", { bubbles: true }));
        card._restoringState = false;
        cardRestoredVariantIds.add(variantId);
      }, 0);
    }

    restoreCardStates(savedStates) {
      this.ghostSelections = [];
      this.refreshCardRefs();
      if (!savedStates) return;

      const normalized = savedStates;
      const cardEntries = Array.isArray(normalized?.cardEntries)
        ? normalized.cardEntries
        : Array.isArray(normalized)
          ? normalized
          : [];
      const ghostItems = Array.isArray(normalized?.ghostItems)
        ? normalized.ghostItems
        : [];

      const restoredKeys = new Set();
      const cardRestoredVariantIds = new Set();
      const ghostIds = new Set();

      cardEntries.forEach((entry) => {
        const entryItems = Array.isArray(entry?.items)
          ? entry.items
          : Object.entries(entry?.variantQtyById || {}).map(([vId, qty]) => ({
              id: parseInt(vId, 10) || 0,
              quantity: qty,
            }));
        if (!entryItems.length) return;

        const matchedCard = !entry?.key
          ? null
          : this.cards.find((card) => {
              if (typeof card.setVariantQty !== "function") return false;
              const cardKey = this.getCardKey(card);
              return cardKey && cardKey === entry.key;
            });

        if (matchedCard && !restoredKeys.has(entry.key)) {
          entryItems.forEach((it) => {
            const variantId = toInt(it?.id, 0) || 0;
            const qty = toInt(it?.quantity, 0) || 0;
            if (!variantId || qty <= 0) return;
            this._restoreCardVariantQty(
              matchedCard,
              variantId,
              qty,
              cardRestoredVariantIds,
            );
          });
          restoredKeys.add(entry.key);
          return;
        }

        entryItems.forEach((it) => {
          const variantId = toInt(it?.id, 0) || 0;
          const qty = toInt(it?.quantity, 0) || 0;
          const strId = String(variantId);
          if (
            !variantId ||
            qty <= 0 ||
            cardRestoredVariantIds.has(variantId) ||
            ghostIds.has(strId)
          )
            return;
          ghostIds.add(strId);
          this.ghostSelections.push({
            id: variantId,
            quantity: qty,
            priceCents: toInt(it?.priceCents, 0) || 0,
            originalPriceCents:
              toInt(it?.originalPriceCents, 0) > 0
                ? toInt(it.originalPriceCents, 0)
                : toInt(it?.priceCents, 0) || 0,
            campaignId: String(it?.campaignId || "").trim(),
            productTitle: String(it?.productTitle || "").trim(),
            variantTitle: String(it?.variantTitle || "").trim(),
            imageSrc: String(it?.imageSrc || "").trim(),
            imageAlt: String(it?.imageAlt || "").trim(),
            productKey: String(entry?.key || "").trim(),
          });
        });
      });

      ghostItems.forEach((it) => {
        const variantId = toInt(it?.id, 0) || 0;
        const qty = toInt(it?.quantity, 0) || 0;
        const strId = String(variantId);
        const ghostProductKey = String(it?.productKey || "").trim();

        if (
          !variantId ||
          qty <= 0 ||
          cardRestoredVariantIds.has(variantId) ||
          ghostIds.has(strId)
        )
          return;

        if (ghostProductKey) {
          const matchedGhostCard = this.cards.find((card) => {
            if (typeof card.setVariantQty !== "function") return false;
            const cardKey = this.getCardKey(card);
            return cardKey && cardKey === ghostProductKey;
          });

          if (matchedGhostCard) {
            this._restoreCardVariantQty(
              matchedGhostCard,
              variantId,
              qty,
              cardRestoredVariantIds,
            );
            return;
          }
        }

        ghostIds.add(strId);
        this.ghostSelections.push({
          id: variantId,
          quantity: qty,
          priceCents: toInt(it?.priceCents, 0) || 0,
          originalPriceCents:
            toInt(it?.originalPriceCents, 0) > 0
              ? toInt(it.originalPriceCents, 0)
              : toInt(it?.priceCents, 0) || 0,
          campaignId: String(it?.campaignId || "").trim(),
          productTitle: String(it?.productTitle || "").trim(),
          variantTitle: String(it?.variantTitle || "").trim(),
          imageSrc: String(it?.imageSrc || "").trim(),
          imageAlt: String(it?.imageAlt || "").trim(),
          productKey: ghostProductKey,
        });
      });

      this.updateTotals();
    }

    refreshCardRefs() {
      this.cards = Array.from(this.querySelectorAll("[data-card]"));
    }

    getCardKey(card) {
      return (
        card?.dataset?.productKey ||
        card?.dataset?.productHandle ||
        card?.querySelector?.("[data-product-handle]")?.dataset
          ?.productHandle ||
        ""
      );
    }

    renderFiltersFromDoc(doc) {
      const incomingFilters = doc.querySelector(".bulk-sale-filters");
      const currentFilters = this.querySelector(".bulk-sale-filters");
      if (!incomingFilters || !currentFilters) return;
      currentFilters.replaceWith(incomingFilters);
      this.filterSelects = Array.from(
        this.querySelectorAll(
          ".bulk-sale-filter__select[data-filter-url-select]",
        ),
      );
      this.filterSelects.forEach((select) => {
        if (select.dataset.filterInitialized === "true") return;
        select.dataset.filterInitialized = "true";
        select.addEventListener("change", (event) =>
          this.onFiltersChanged(event),
        );
      });
      this.setupSelectMirrors();
    }

    renderGridAndPaginationFromDoc(doc) {
      const sectionId = this.getSectionId();
      const incomingContainer = doc.getElementById("ProductGridContainer");
      const currentContainer = this.querySelector("#ProductGridContainer");
      if (incomingContainer && currentContainer) {
        currentContainer.innerHTML = incomingContainer.innerHTML;
      }
      this.grid = this.querySelector("#product-grid");
      this.pagination = this.querySelector("pagination-theme.pagination");
      if (this.pagination && sectionId) {
        this.pagination.dataset.section = sectionId;
      }
      this.setupPagination();
    }

    setGridLoading(isLoading) {
      if (!this.grid) return;
      if (isLoading) {
        this.grid.classList.add("loading");
      } else {
        this.grid.classList.remove("loading");
      }
    }

    pushUrlState(nextUrl) {
      const search = nextUrl.search.slice(1);
      history.pushState(
        { searchParams: search },
        "",
        nextUrl.pathname + nextUrl.search + nextUrl.hash,
      );
    }

    async renderPageByUrl(nextUrl, updateUrl = true) {
      const sectionId = this.getSectionId();
      if (!sectionId) {
        window.location.assign(nextUrl.toString());
        return;
      }
      const savedStates = this.serializeCardStates();
      const fetchUrl = new URL(nextUrl.toString());
      fetchUrl.searchParams.set("section_id", sectionId);

      this.setGridLoading(true);

      try {
        const response = await fetch(fetchUrl.toString(), {
          headers: { Accept: "text/html" },
        });
        if (!response.ok) {
          window.location.assign(nextUrl.toString());
          return;
        }
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, "text/html");

        this.renderFiltersFromDoc(doc);
        this.renderGridAndPaginationFromDoc(doc);
        await customElements.whenDefined("bulk-sale-product-card");
        this.restoreCardStates(savedStates);
        if (updateUrl) {
          this.pushUrlState(nextUrl);
        }
        if (typeof dispatchCustomEvent === "function") {
          dispatchCustomEvent("bulk-sale:filters-changed", {
            url: nextUrl.toString(),
          });
        }
      } catch (err) {
        window.location.assign(nextUrl.toString());
      } finally {
        this.setGridLoading(false);
      }
    }

    setupSelectMirrors() {
      this.querySelectorAll(".bulk-sale-filter").forEach((filter) => {
        const select = filter.querySelector(".bulk-sale-filter__select");
        const text = filter.querySelector(".bulk-sale-filter__text");
        if (!select || !text) return;
        if (select.dataset.mirrorInitialized === "true") return;
        select.dataset.mirrorInitialized = "true";
        const sync = () => {
          text.textContent =
            select.options[select.selectedIndex]?.textContent || "";
        };
        select.addEventListener("change", sync);
        sync();
      });
    }

    setupCardChangeBridge() {
      if (this.dataset.cardBridgeInitialized === "true") return;
      this.dataset.cardBridgeInitialized = "true";
      this.addEventListener("bulk-sale-card:change", () => {
        this.purgeGhostsFromVisibleCards();
        this.updateTotals();
      });
    }

    purgeGhostsFromVisibleCards() {
      if (!Array.isArray(this.ghostSelections) || !this.ghostSelections.length)
        return;
      const visibleIds = new Set();
      this.cards.forEach((card) => {
        if (card.variantQtyById instanceof Map) {
          card.variantQtyById.forEach((qty, vId) => {
            if (qty > 0) visibleIds.add(String(vId));
          });
        }
        const cardVid =
          parseInt(
            card.dataset?.variantId ||
              card.querySelector?.("[data-variant-select]")?.value ||
              "0",
            10,
          ) || 0;
        const cardQty =
          parseInt(
            card.dataset?.qty ||
              card.querySelector?.("[data-qty]")?.dataset?.qty ||
              "0",
            10,
          ) || 0;
        if (cardVid > 0 && cardQty > 0) visibleIds.add(String(cardVid));
      });
      if (!visibleIds.size) return;
      const before = this.ghostSelections.length;
      this.ghostSelections = this.ghostSelections.filter(
        (it) => !visibleIds.has(String(toInt(it?.id, 0) || 0)),
      );
    }

    setupPagination() {
      if (!this.pagination || !this.grid) return;

      if (this.pagination.classList.contains("pagination-type--loadmore")) {
        const btn = this.pagination.querySelector(".load-more");
        if (!btn) return;
        if (btn.dataset.bulkSalePaginationInitialized === "true") return;
        btn.dataset.bulkSalePaginationInitialized = "true";
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          this.loadNextPage();
        });
      }

      if (this.pagination.classList.contains("pagination-type--infinite")) {
        if (this.paginationObserver) return;
        this.paginationObserver = new IntersectionObserver(
          (entries) => {
            if (!entries[0] || entries[0].intersectionRatio !== 1) return;
            this.loadNextPage();
          },
          { threshold: [0, 1] },
        );
        this.paginationObserver.observe(this.pagination);
      }
    }

    getNextPageUrl() {
      if (!this.pagination) return null;
      const hasNext = this.pagination.dataset.hasNext === "true";
      if (!hasNext) return null;
      const nextUrl = this.pagination.dataset.nextUrl;
      if (nextUrl) return nextUrl;

      const currentUrl = new URL(window.location.href);
      const currentPage = parseInt(
        currentUrl.searchParams.get("page") || "1",
        10,
      );
      const nextPage = Number.isFinite(currentPage) ? currentPage + 1 : 2;
      currentUrl.searchParams.set("page", String(nextPage));
      return currentUrl.toString();
    }

    setPaginationLoading(isLoading) {
      if (!this.pagination) return;
      if (isLoading) {
        this.pagination.setAttribute("loading", "true");
      } else {
        this.pagination.removeAttribute("loading");
      }
    }

    stopPagination() {
      if (this.paginationObserver) {
        this.paginationObserver.disconnect();
        this.paginationObserver = null;
      }
      const btn = this.pagination?.querySelector(".load-more");
      if (btn) btn.classList.add("visually-hidden");
    }

    updatePaginationFromDoc(doc) {
      if (!this.pagination) return;
      const sectionId = this.pagination.dataset.section;
      if (!sectionId) return;

      const nextPagination = doc.querySelector(
        `pagination-theme[data-section="${CSS.escape(sectionId)}"]`,
      );

      if (!nextPagination) {
        this.pagination.dataset.hasNext = "false";
        this.pagination.removeAttribute("data-next-url");
        this.stopPagination();
        return;
      }

      const keepLoading = this.pagination.getAttribute("loading");
      if (this.paginationObserver) {
        this.paginationObserver.disconnect();
        this.paginationObserver = null;
      }

      this.pagination.replaceWith(nextPagination);
      this.pagination = nextPagination;

      if (keepLoading) {
        this.pagination.setAttribute("loading", keepLoading);
      }

      if (this.pagination.dataset.hasNext !== "true") {
        this.stopPagination();
      }

      this.setupPagination();
    }

    appendProductsFromDoc(doc) {
      if (!this.grid || !this.pagination) return false;
      const sectionId = this.pagination.dataset.section;
      if (!sectionId) return false;

      const nextGrid = doc.querySelector(
        `#product-grid[data-id="${CSS.escape(sectionId)}"]`,
      );
      if (!nextGrid) return false;

      const nextCards = Array.from(nextGrid.querySelectorAll("[data-card]"));
      if (!nextCards.length) return false;

      const frag = document.createDocumentFragment();
      nextCards.forEach((card) => {
        frag.appendChild(card);
        this.cards.push(card);
      });
      this.grid.appendChild(frag);

      return true;
    }

    async loadNextPage() {
      const url = this.getNextPageUrl();
      if (!url) {
        this.stopPagination();
        return;
      }
      if (this.isLoadingNextPage) return;

      const savedStates = this.serializeCardStates();
      this.isLoadingNextPage = true;
      this.setPaginationLoading(true);

      try {
        const response = await fetch(url, { headers: { Accept: "text/html" } });
        if (!response.ok) {
          this.stopPagination();
          return;
        }
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, "text/html");

        const appended = this.appendProductsFromDoc(doc);
        this.updatePaginationFromDoc(doc);

        if (!appended) {
          this.pagination.dataset.hasNext = "false";
          this.pagination.removeAttribute("data-next-url");
          this.stopPagination();
        }

        await customElements.whenDefined("bulk-sale-product-card");
        this.restoreCardStates(savedStates);

        if (typeof dispatchCustomEvent === "function") {
          dispatchCustomEvent("pagination:page-changed", { url });
        }
      } catch (err) {
        console.error("[BulkSalePage] Failed to load next page:", err);
        this.stopPagination();
      } finally {
        this.isLoadingNextPage = false;
        this.setPaginationLoading(false);
      }
    }

    setupActions() {
      if (this.atcBtn) {
        this.atcBtn.addEventListener("click", () => this.addSelectedToCart());
      }
      if (this.buyNowBtn) {
        this.buyNowBtn.addEventListener("click", () =>
          this.redirectSelectedToCheckout(),
        );
      }
    }

    setupDetails() {
      if (!this.itemsBtn || !this.detailsEl || !this.detailsGridEl) return;
      if (this.dataset.detailsInitialized === "true") return;
      this.dataset.detailsInitialized = "true";

      this.itemsBtn.addEventListener("click", () => {
        if (this.getSelectedItemsCount() <= 0) return;
        this.toggleDetails();
      });

      if (this.detailsCloseBtn) {
        this.detailsCloseBtn.addEventListener("click", () =>
          this.toggleDetails(false),
        );
      }

      this.detailsGridEl.addEventListener("click", (e) => {
        const btn = e.target?.closest?.("[data-remove-variant]");
        if (!btn) return;
        const variantId = parseInt(btn.dataset.removeVariant || "0", 10) || 0;
        const cardIndexRaw = btn.dataset.cardIndex || "";
        const cardIndex =
          cardIndexRaw === "" ? NaN : parseInt(cardIndexRaw, 10) || 0;
        if (!variantId) return;
        if (Number.isNaN(cardIndex) || cardIndex < 0) {
          if (Array.isArray(this.ghostSelections)) {
            const before = this.ghostSelections.length;
            this.ghostSelections = this.ghostSelections.filter(
              (g) => String(toInt(g?.id, 0) || 0) !== String(variantId),
            );
            if (this.ghostSelections.length !== before) {
              this.updateTotals();
              if (this.isDetailsOpen) {
                this.renderDetails({
                  force: true,
                  count: this.getSelectedItemsCount(),
                  metricValue: this.lastMetricValue,
                });
              }
            }
          }
          return;
        }
        const card = this.cards[cardIndex];
        if (!card || typeof card.setVariantQty !== "function") return;
        card.setVariantQty(variantId, 0);
      });

      this.onDocumentKeydown = (e) => {
        if (!this.isDetailsOpen) return;
        if (e.key !== "Escape") return;
        this.toggleDetails(false);
      };
      document.addEventListener("keydown", this.onDocumentKeydown);
    }

    toggleDetails(next) {
      const open = typeof next === "boolean" ? next : !this.isDetailsOpen;
      this.isDetailsOpen = open;
      if (this.detailsEl) {
        this.detailsEl.setAttribute("aria-hidden", open ? "false" : "true");
      }
      if (this.itemsBtn) {
        this.itemsBtn.classList.toggle("is-open", open);
        this.itemsBtn.setAttribute("aria-expanded", open ? "true" : "false");
      }
      if (open)
        this.renderDetails({
          force: true,
          count: this.getSelectedItemsCount(),
          metricValue: this.lastMetricValue,
        });
    }

    getDetailsEntries() {
      const entries = [];
      this.cards.forEach((card, idx) => {
        if (typeof card.getAllSelectedItems !== "function") return;
        const productTitle =
          card.querySelector(".bulk-sale-card__title")?.textContent?.trim() ||
          "";
        const imgEl = card.querySelector(".bulk-sale-card__image");
        const defaultSrc =
          imgEl?.dataset?.defaultSrc || imgEl?.getAttribute("src") || "";
        const defaultAlt =
          imgEl?.dataset?.defaultAlt || imgEl?.getAttribute("alt") || "";
        const select = card.querySelector("[data-variant-select]");
        const options = select ? Array.from(select.options || []) : [];
        const optionByVariantId = new Map();
        options.forEach((opt) => {
          const variantId = parseInt(opt.value || "0", 10) || 0;
          if (variantId) optionByVariantId.set(variantId, opt);
        });

        card.getAllSelectedItems().forEach((it) => {
          const variantId = parseInt(String(it.id || "0"), 10) || 0;
          const quantity = parseInt(String(it.quantity || "0"), 10) || 0;
          if (!variantId || quantity <= 0) return;

          const opt = optionByVariantId.get(variantId) || null;
          const variantTitle = opt?.textContent?.trim() || "";
          const imageSrc = opt?.dataset?.variantImageSrc || defaultSrc;
          const imageAlt =
            opt?.dataset?.variantImageAlt || variantTitle || defaultAlt;
          const priceCents = parseInt(String(it.priceCents || "0"), 10) || 0;

          entries.push({
            cardIndex: idx,
            variantId,
            quantity,
            priceCents,
            productTitle,
            variantTitle,
            imageSrc,
            imageAlt,
          });
        });
      });

      if (Array.isArray(this.ghostSelections)) {
        const seenIds = new Set(entries.map((e) => String(e.variantId)));
        this.ghostSelections.forEach((it) => {
          const variantId = toInt(it?.id, 0) || 0;
          const quantity = toInt(it?.quantity, 0) || 0;
          if (!variantId || quantity <= 0) return;
          if (seenIds.has(String(variantId))) return;
          seenIds.add(String(variantId));
          entries.push({
            cardIndex: -1,
            variantId,
            quantity,
            priceCents: toInt(it?.priceCents, 0) || 0,
            productTitle: String(it?.productTitle || "").trim(),
            variantTitle: String(it?.variantTitle || "").trim(),
            imageSrc: String(it?.imageSrc || "").trim(),
            imageAlt: String(it?.imageAlt || "").trim(),
            isGhost: true,
          });
        });
      }

      return entries;
    }

    renderDetails(
      { force, metricValue } = {
        force: false,
        metricValue: null,
      },
    ) {
      if (!this.detailsGridEl) return;
      const entries = this.getDetailsEntries();
      const currentMetric =
        typeof metricValue === "number" ? metricValue : this.lastMetricValue;
      const tier = this.getCurrentTier(currentMetric);
      const valueType = this.discountValueType || this.getDiscountValueType();
      const discountSignatureValue =
        valueType === "amount"
          ? toInt(tier?.amountOffCents, 0) || 0
          : toFloat(tier?.percent, 0) || 0;
      const signature = `${valueType}:${discountSignatureValue}|${entries
        .map((e) => `${e.cardIndex}:${e.variantId}:${e.quantity}`)
        .join("|")}`;

      if (!force && signature === this.detailsSignature) return;
      this.detailsSignature = signature;

      this.detailsGridEl.textContent = "";
      entries.forEach((entry) => {
        const item = document.createElement("div");
        item.className = "bulk-sale-sticky__detail-item";

        const thumb = document.createElement("div");
        thumb.className = "bulk-sale-sticky__detail-thumb";

        const img = document.createElement("img");
        img.loading = "lazy";
        if (entry.imageSrc) img.src = entry.imageSrc;
        img.alt = entry.imageAlt || entry.productTitle || "";
        thumb.appendChild(img);

        const body = document.createElement("div");
        body.className = "bulk-sale-sticky__detail-body";

        const top = document.createElement("div");
        top.className = "bulk-sale-sticky__detail-top";

        const title = document.createElement("div");
        title.className = "bulk-sale-sticky__detail-title";
        title.textContent = entry.productTitle;
        title.title = entry.productTitle;

        const prices = document.createElement("div");
        prices.className = "bulk-sale-sticky__detail-prices";

        const originalPriceCents = entry.priceCents;
        const lineId = String(entry.variantId || "").trim();
        const lineDiscountCents =
          toInt(this.lineDiscountCentsById?.[lineId], 0) || 0;
        const discountedLineSubtotalCents = Math.max(
          0,
          originalPriceCents * entry.quantity - lineDiscountCents,
        );
        const discountedUnitPriceCents =
          entry.quantity > 0
            ? Math.round(discountedLineSubtotalCents / entry.quantity)
            : 0;

        if (
          lineDiscountCents > 0 &&
          discountedUnitPriceCents < originalPriceCents
        ) {
          const priceCompare = document.createElement("span");
          priceCompare.className = "bulk-sale-sticky__detail-price-compare";
          priceCompare.textContent = this.formatMoneyTiered(originalPriceCents);
          prices.appendChild(priceCompare);
        }

        const priceMain = document.createElement("span");
        priceMain.className = "bulk-sale-sticky__detail-price-main";
        priceMain.textContent = `${this.formatMoneyTiered(discountedUnitPriceCents)} × ${entry.quantity}`;

        prices.appendChild(priceMain);

        top.appendChild(title);
        top.appendChild(prices);

        const bottom = document.createElement("div");
        bottom.className = "bulk-sale-sticky__detail-bottom";

        const variant = document.createElement("div");
        variant.className = "bulk-sale-sticky__detail-variant";
        variant.textContent = entry.variantTitle;

        const remove = document.createElement("button");
        remove.type = "button";
        remove.className = "bulk-sale-sticky__detail-remove";
        remove.textContent = this.removeLabel;
        remove.dataset.removeVariant = String(entry.variantId);
        remove.dataset.cardIndex = String(entry.cardIndex);

        bottom.appendChild(variant);
        bottom.appendChild(remove);

        body.appendChild(top);
        body.appendChild(bottom);

        item.appendChild(thumb);
        item.appendChild(body);

        this.detailsGridEl.appendChild(item);
      });

      if (!entries.length) this.toggleDetails(false);
    }

    getSelectedItems() {
      return this.getSelectionSummary().items;
    }

    getSelectedSubtotalCents() {
      return this.getSelectionSummary().subtotalCents;
    }

    getSelectedItemsCount() {
      return this.getSelectionSummary().count;
    }

    getSelectionSummary() {
      const items = [];
      let count = 0;
      let subtotalCents = 0;
      let originalSubtotalCents = 0;
      let selectionError = "";
      const seenVariantIds = new Set();

      this.cards.forEach((card) => {
        if (typeof card.getAllSelectedSummary === "function") {
          const s = card.getAllSelectedSummary();
          if (s && Array.isArray(s.items)) {
            s.items.forEach((it) => {
              if (!it || !it.id || it.quantity <= 0) return;
              if (selectionError) return;
              const variantId = toInt(it.id, 0) || 0;
              if (!variantId) return;
              const itemCampaignIds = parseCampaignIds(it.campaignId).filter(
                (discountId) => this.discountCampaignIds.includes(discountId),
              );
              if (!itemCampaignIds.length) {
                selectionError = this.selectionErrorLabel || "";
                return;
              }
              const quantity = toInt(it.quantity, 0) || 0;
              const priceCents = toInt(it.priceCents, 0) || 0;
              const originalPriceCents =
                toInt(it.originalPriceCents, 0) > 0
                  ? toInt(it.originalPriceCents, 0)
                  : priceCents;
              seenVariantIds.add(String(variantId));
              items.push({
                id: variantId,
                quantity,
                priceCents,
                originalPriceCents,
                campaignIds: itemCampaignIds,
              });
              count += quantity;
              subtotalCents += priceCents * quantity;
              originalSubtotalCents += originalPriceCents * quantity;
            });
          }
          return;
        }

        if (selectionError) return;
        const qtyRoot = card.querySelector?.("[data-qty]");
        const variantSelect = card.querySelector?.("[data-variant-select]");
        const selectedOption =
          variantSelect?.options?.[variantSelect.selectedIndex || 0] || null;
        const qty = toInt(card.dataset.qty || qtyRoot?.dataset?.qty, 0) || 0;
        if (qty <= 0) return;

        const variantId =
          toInt(card.dataset.variantId || variantSelect?.value, 0) || 0;
        if (!variantId) return;

        const itemCampaignIdRaw = (
          card.dataset.variantCampaignId ||
          selectedOption?.dataset?.variantCampaignId ||
          card.dataset.productCampaignId ||
          ""
        ).trim();
        const itemCampaignIds = parseCampaignIds(itemCampaignIdRaw).filter(
          (discountId) => this.discountCampaignIds.includes(discountId),
        );
        if (!itemCampaignIds.length) {
          selectionError = this.selectionErrorLabel || "";
          return;
        }

        let priceCents = toInt(card.dataset.variantPriceCents, NaN);
        if (!Number.isFinite(priceCents)) {
          priceCents = toInt(selectedOption?.dataset?.variantPriceCents, 0);
        }
        let compareAtPriceCents = toInt(
          card.dataset.variantCompareAtPriceCents,
          NaN,
        );
        if (!Number.isFinite(compareAtPriceCents)) {
          compareAtPriceCents = toInt(
            selectedOption?.dataset?.variantCompareAtPriceCents,
            0,
          );
        }
        const originalPriceCents =
          compareAtPriceCents > priceCents ? compareAtPriceCents : priceCents;
        seenVariantIds.add(String(variantId));
        items.push({
          id: variantId,
          quantity: qty,
          priceCents,
          originalPriceCents,
          campaignIds: itemCampaignIds,
        });
        count += qty;
        subtotalCents += priceCents * qty;
        originalSubtotalCents += originalPriceCents * qty;
      });

      if (!selectionError && Array.isArray(this.ghostSelections)) {
        this.ghostSelections.forEach((it) => {
          if (!it || !it.id || it.quantity <= 0) return;
          const variantId = toInt(it.id, 0) || 0;
          if (!variantId) return;
          if (seenVariantIds.has(String(variantId))) return;
          const itemCampaignIds = parseCampaignIds(it.campaignId).filter(
            (discountId) => this.discountCampaignIds.includes(discountId),
          );
          if (!itemCampaignIds.length) return;
          const quantity = toInt(it.quantity, 0) || 0;
          const priceCents = toInt(it.priceCents, 0) || 0;
          const originalPriceCents =
            toInt(it.originalPriceCents, 0) > 0
              ? toInt(it.originalPriceCents, 0)
              : priceCents;
          seenVariantIds.add(String(variantId));
          items.push({
            id: variantId,
            quantity,
            priceCents,
            originalPriceCents,
            campaignIds: itemCampaignIds,
          });
          count += quantity;
          subtotalCents += priceCents * quantity;
          originalSubtotalCents += originalPriceCents * quantity;
        });
      }

      return {
        items,
        count,
        subtotalCents,
        originalSubtotalCents,
        selectionError,
      };
    }

    resolveDiscountSolution(summary) {
      if (!summary?.items?.length || !this.discountCampaigns.length)
        return null;
      const rawLines = summary.items
        .map((item) => ({
          id: item.id,
          quantity: toInt(item.quantity, 0) || 0,
          subtotalCents:
            (toInt(item.priceCents, 0) || 0) * (toInt(item.quantity, 0) || 0),
          campaignIds: (Array.isArray(item.campaignIds) ? item.campaignIds : [])
            .map((discountId) => String(discountId || "").trim())
            .filter(Boolean),
        }))
        .filter((line) => line.quantity > 0 && line.campaignIds.length);

      if (!rawLines.length) return null;

      const assignmentSolution = optimizeDiscountAssignments(
        this.discountCampaigns,
        rawLines,
        { mode: "single_best" },
      );
      const lineInfos = assignmentSolution?.lineInfos || [];

      if (!lineInfos.length) return null;

      const assignmentSummary =
        buildCampaignResultsFromSolution(assignmentSolution);
      const campaignResults = assignmentSummary.campaignResults || [];
      const totalDiscountCents = assignmentSummary.totalDiscountCents || 0;
      let displayCampaign = selectBestCampaignResult(campaignResults);
      const lineDiscountCentsById = {};

      if (!displayCampaign) {
        const fallbackCandidates = this.discountCampaigns
          .map((campaign, campaignIndex) => {
            const groupLineIndexes = lineInfos
              .map((line, lineIndex) =>
                line.applicableCampaignIndexes.includes(campaignIndex)
                  ? lineIndex
                  : -1,
              )
              .filter((lineIndex) => lineIndex >= 0);
            return buildCampaignResult(campaign, groupLineIndexes, lineInfos, {
              campaignIndex,
            });
          })
          .filter(Boolean);

        displayCampaign =
          selectBestCampaignResult(fallbackCandidates) ||
          fallbackCandidates[0] ||
          null;
      }

      campaignResults.forEach((result) => {
        const groupLineIndexes = Array.isArray(result?.groupLineIndexes)
          ? result.groupLineIndexes
          : [];
        const groupDiscountCents = toInt(result?.discountCents, 0) || 0;
        if (!groupLineIndexes.length || groupDiscountCents <= 0) return;

        const groupSubtotalCents = groupLineIndexes.reduce(
          (sum, lineIndex) =>
            sum + (toInt(lineInfos[lineIndex]?.subtotalCents, 0) || 0),
          0,
        );
        if (groupSubtotalCents <= 0) return;

        const shares = groupLineIndexes
          .map((lineIndex) => {
            const lineSubtotalCents = Math.max(
              0,
              toInt(lineInfos[lineIndex]?.subtotalCents, 0) || 0,
            );
            const numerator = groupDiscountCents * lineSubtotalCents;
            const base = Math.floor(numerator / groupSubtotalCents);
            const remainder = numerator % groupSubtotalCents;
            return { lineIndex, base, remainder };
          })
          .sort((a, b) => b.remainder - a.remainder);

        let allocated = shares.reduce(
          (sum, s) => sum + (toInt(s.base, 0) || 0),
          0,
        );
        let remaining = groupDiscountCents - allocated;

        for (let i = 0; i < shares.length && remaining > 0; i += 1) {
          shares[i].base += 1;
          remaining -= 1;
        }

        shares.forEach(({ lineIndex, base }) => {
          const lineId = String(lineInfos[lineIndex]?.id || "").trim();
          const cents = toInt(base, 0) || 0;
          if (!lineId || cents <= 0) return;
          lineDiscountCentsById[lineId] =
            (toInt(lineDiscountCentsById[lineId], 0) || 0) + cents;
        });
      });

      return {
        totalDiscountCents,
        displayCampaign,
        lineDiscountCentsById,
      };
    }

    getTierThreshold(tier) {
      return getTierThreshold(tier, this.discountBasis);
    }

    getCurrentTier(metricValue) {
      return getCurrentTier(this.tiers, this.discountBasis, metricValue);
    }

    getNextTier(metricValue) {
      return getNextTier(this.tiers, this.discountBasis, metricValue);
    }

    getProgressTiers() {
      const valueType = this.discountValueType || this.getDiscountValueType();
      return this.tiers
        .map((t) => ({
          threshold: this.getTierThreshold(t),
          percent: toFloat(t?.percent, 0),
          amountOffCents: toInt(t?.amountOffCents, 0),
          basis: this.discountBasis,
          valueType,
        }))
        .filter((t) => t.threshold > 0)
        .sort((a, b) => a.threshold - b.threshold);
    }

    updateTotals() {
      const summary = this.getSelectionSummary();
      const count = summary.count;
      const subtotalCents = summary.subtotalCents;
      const discountSolution = this.resolveDiscountSolution(summary);
      const activeCampaign =
        discountSolution?.displayCampaign?.campaign || null;
      if (activeCampaign) {
        this.activeCampaignId = activeCampaign.discountId;
        this.discountBasis = activeCampaign.basis;
        this.discountValueType =
          String(activeCampaign.valueType || "").toLowerCase() === "amount"
            ? "amount"
            : "percentage";
        this.tiers = activeCampaign.tiers;
      } else if (this.discountCampaigns.length) {
        this.activeCampaignId = this.discountCampaigns[0].discountId;
        this.discountBasis = this.discountCampaigns[0].basis;
        this.discountValueType =
          String(this.discountCampaigns[0].valueType || "").toLowerCase() ===
          "amount"
            ? "amount"
            : this.getDiscountValueType();
        this.tiers = this.discountCampaigns[0].tiers;
      }

      this.handleActionError(summary.selectionError || false);
      // 使用 summary.subtotalCents（variant.price × qty）与购物车保持一致，
      // 不用 originalSubtotalCents（max(compare_at, price) × qty）——有 compare_at_price
      // 时会导致 metricValue 被高估，进度条位置和 currentTier 与实际折扣计算不匹配。
      const metricValue =
        this.discountBasis === "amount"
          ? toInt(summary.subtotalCents, 0) || 0
          : count;
      this.lastMetricValue = metricValue;
      const tier = this.getCurrentTier(metricValue);
      const discountCents = discountSolution?.totalDiscountCents || 0;
      this.lineDiscountCentsById =
        discountSolution?.lineDiscountCentsById || {};
      const totalCents = subtotalCents - discountCents;

      const hasSelection = count > 0;
      if (this.stickyEl) this.stickyEl.hidden = false;
      this.classList.toggle("bulk-sale-page--sticky-active", hasSelection);
      document.body.classList.toggle("bulk-sale-sticky-active", hasSelection);

      if (this.itemsBtn) this.itemsBtn.disabled = count <= 0;
      if (!hasSelection && this.isDetailsOpen) this.toggleDetails(false);
      if (this.atcBtn) {
        this.atcBtn.disabled = !hasSelection;
        this.atcBtn.setAttribute(
          "aria-disabled",
          hasSelection ? "false" : "true",
        );
      }
      if (this.buyNowBtn) {
        this.buyNowBtn.disabled = !hasSelection;
        this.buyNowBtn.setAttribute(
          "aria-disabled",
          hasSelection ? "false" : "true",
        );
      }

      const itemLabel =
        count === 1 ? this.itemLabelSingular : this.itemLabelPlural;
      const countText = `${count} ${itemLabel}`;
      if (this.totalItemsEl) {
        this.totalItemsEl.textContent = countText;
        this.totalItemsEl.classList.toggle("is-highlighted", count > 0);
      }
      if (this.subtotalTitleEl) {
        this.subtotalTitleEl.textContent = `${this.subtotalLabel} · ${countText}`;
      }
      if (this.totalPriceEl) {
        this.totalPriceEl.textContent = this.formatMoneyTiered(totalCents);
      }

      if (this.discountPillEl && this.discountAmountEl) {
        if (discountCents > 0) {
          this.discountPillEl.hidden = false;
          this.discountAmountEl.textContent = `- ${this.formatMoneyTiered(discountCents)}`;
        } else {
          this.discountPillEl.hidden = true;
        }
      }

      const messages = this.getDiscountMessage({
        count,
        subtotalCents,
        metricValue,
        tier,
      });
      if (this.desktopMessageEl) {
        const text = messages?.desktop || "";
        this.desktopMessageEl.textContent = text;
        this.desktopMessageEl.hidden = !text;
      }
      if (this.mobileMessageEl) {
        const text = messages?.mobile || "";
        this.mobileMessageEl.textContent = text;
        this.mobileMessageEl.hidden = !text;
      }

      const progressTiers = this.getProgressTiers();
      if (this.desktopProgress)
        this.desktopProgress.update({
          value: metricValue,
          tiers: progressTiers,
          offLabel: this.tooltipOffLabel,
        });
      if (this.mobileProgress)
        this.mobileProgress.update({
          value: metricValue,
          tiers: progressTiers,
          offLabel: this.tooltipOffLabel,
        });

      if (this.isDetailsOpen)
        this.renderDetails({ count, metricValue: this.lastMetricValue });
    }

    getDiscountMessage({ metricValue, tier }) {
      const next = this.getNextTier(metricValue);
      if (!next) {
        const valueType = this.discountValueType || this.getDiscountValueType();
        const percent = toFloat(tier?.percent, 0);
        const amountOffCents = toInt(tier?.amountOffCents, 0);
        const text =
          valueType === "amount"
            ? amountOffCents > 0
              ? this.messageUnlockedTemplate.replace(
                  "{{ off_value }}",
                  this.formatMoneyTiered(amountOffCents),
                )
              : ""
            : percent > 0
              ? this.messageUnlockedTemplate.replace(
                  "{{ off_value }}",
                  percent + "%",
                )
              : "";
        return { desktop: text, mobile: text };
      }

      if (metricValue <= 0) {
        const valueType = this.discountValueType || this.getDiscountValueType();
        const nextThreshold = this.getTierThreshold(next);
        const thresholdText =
          this.discountBasis === "amount"
            ? this.formatMoneyTiered(nextThreshold)
            : String(nextThreshold);
        const unit =
          this.discountBasis === "amount" ? "" : ` ${this.itemLabelPlural}`;
        const text =
          valueType === "amount"
            ? this.messageAddTemplate
                .replace("{{ value }}", thresholdText)
                .replace("{{ unit }}", unit)
                .replace(
                  "{{ off_value }}",
                  this.formatMoneyTiered(toInt(next.amountOffCents, 0)),
                )
            : this.messageAddTemplate
                .replace("{{ value }}", thresholdText)
                .replace("{{ unit }}", unit)
                .replace("{{ off_value }}", next.percent + "%");
        return {
          desktop: text,
          mobile: text,
        };
      }

      const valueType = this.discountValueType || this.getDiscountValueType();
      const currentPercent = toFloat(tier?.percent, 0);
      const nextThreshold = this.getTierThreshold(next);
      const need = Math.max(0, nextThreshold - metricValue);
      const needText =
        this.discountBasis === "amount"
          ? this.formatMoneyTiered(need)
          : String(need);
      const unit =
        this.discountBasis === "amount" ? "" : ` ${this.itemLabelPlural}`;
      const text =
        valueType === "amount"
          ? this.messageUpgradeTemplate
              .replace(
                "{{ current_off_value }}",
                this.formatMoneyTiered(toInt(tier?.amountOffCents, 0)),
              )
              .replace("{{ value }}", needText)
              .replace("{{ unit }}", unit)
              .replace(
                "{{ next_off_value }}",
                this.formatMoneyTiered(toInt(next.amountOffCents, 0)),
              )
          : this.messageUpgradeTemplate
              .replace("{{ current_off_value }}", currentPercent + "%")
              .replace("{{ value }}", needText)
              .replace("{{ unit }}", unit)
              .replace("{{ next_off_value }}", next.percent + "%");
      return {
        desktop: text,
        mobile: text,
      };
    }

    formatMoneyTiered(cents) {
      return formatMoneyTiered(cents);
    }

    handleActionError(errorMessage = false) {
      if (!this.actionErrorEl) return;
      this.actionErrorEl.toggleAttribute("hidden", !errorMessage);
      if (this.actionErrorMessageEl) {
        this.actionErrorMessageEl.textContent = errorMessage || "";
      }
    }

    setButtonLoading(button, isLoading) {
      if (!button) return;
      button.disabled = isLoading;
      button.setAttribute("aria-disabled", isLoading ? "true" : "false");
      button.classList.toggle("loading", isLoading);
    }

    getRouteRoot() {
      const rootUrl =
        (window.theme && theme.routes && theme.routes.root_url) ||
        (window.Shopify && Shopify.routes && Shopify.routes.root) ||
        "/";
      return String(rootUrl || "/").replace(/\/+$/, "");
    }

    getDirectCheckoutUrl(items) {
      if (!Array.isArray(items) || !items.length) return "";
      const serializedItems = items
        .map((item) => {
          const variantId = toInt(item?.id, 0) || 0;
          const quantity = toInt(item?.quantity, 0) || 0;
          if (!variantId || quantity <= 0) return "";
          return `${variantId}:${quantity}`;
        })
        .filter(Boolean)
        .join(",");

      if (!serializedItems) return "";

      const root = this.getRouteRoot();
      return new URL(
        `${root}/cart/${serializedItems}`,
        window.location.origin,
      ).toString();
    }

    getSectionsToRender() {
      return [
        { id: "Cart", section: "main-cart", selector: ".thb-cart-form" },
        { id: "Cart-Drawer", section: "cart-drawer", selector: ".cart-drawer" },
        {
          id: "cart-drawer-toggle",
          section: "cart-bubble",
          selector: ".thb-item-count",
        },
      ];
    }

    renderCartSections(parsedState) {
      this.getSectionsToRender().forEach((section) => {
        const root = document.getElementById(section.id);
        if (!root) return;
        const elementToReplace = root.querySelector(section.selector) || root;
        const html =
          parsedState.sections && parsedState.sections[section.section];
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, "text/html");
        const next = section.selector
          ? doc.querySelector(section.selector)
          : doc.body;
        if (!next) return;
        elementToReplace.innerHTML = next.innerHTML;
      });

      if (typeof CartDrawer !== "undefined") {
        new CartDrawer();
      }
      if (typeof Cart !== "undefined") {
        new Cart().renderContents(parsedState);
      }

      if (document.getElementById("Cart-Drawer")) {
        document.body.classList.add("open-cc");
        document.getElementById("Cart-Drawer").classList.add("active");
        if (typeof dispatchCustomEvent === "function") {
          dispatchCustomEvent("cart-drawer:open");
        }
      }
    }

    clearSelected() {
      let touched = false;

      this.cards.forEach((card) => {
        if (!card) return;

        if (typeof card.clearSelection === "function") {
          card.clearSelection();
          touched = true;
          return;
        }

        if (
          typeof card.getAllSelectedItems === "function" &&
          typeof card.setVariantQty === "function"
        ) {
          const items = card.getAllSelectedItems();
          if (Array.isArray(items) && items.length) {
            items.forEach((it) => {
              const variantId = toInt(it?.id, 0) || 0;
              if (!variantId) return;
              card.setVariantQty(variantId, 0);
            });
            touched = true;
          }
          return;
        }

        const qtyRoot = card.querySelector?.("[data-qty]");
        const qtyValueEl = card.querySelector?.("[data-qty-value]");
        if (qtyRoot) qtyRoot.dataset.qty = "0";
        if (card.dataset) card.dataset.qty = "0";
        if (qtyValueEl instanceof HTMLInputElement) {
          qtyValueEl.value = "0";
        } else if (qtyValueEl) {
          qtyValueEl.textContent = "0";
        }
        touched = true;
      });

      if (touched) this.updateTotals();
    }

    redirectSelectedToCheckout() {
      const summary = this.getSelectionSummary();
      const items = summary.items;
      if (!items.length || summary.selectionError) return;

      this.handleActionError();
      this.setButtonLoading(this.buyNowBtn, true);

      const checkoutUrl = this.getDirectCheckoutUrl(items);
      if (!checkoutUrl) {
        this.setButtonLoading(this.buyNowBtn, false);
        this.updateTotals();
        return;
      }

      window.location.assign(checkoutUrl);
    }

    async addSelectedToCart() {
      const summary = this.getSelectionSummary();
      const items = summary.items;
      if (!items.length || summary.selectionError) return;

      this.handleActionError();
      this.setButtonLoading(this.atcBtn, true);

      const config = {
        method: "POST",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          Accept: "application/javascript",
        },
      };

      const formData = new FormData();
      items.forEach((item, index) => {
        formData.append(`items[${index}][id]`, item.id);
        formData.append(`items[${index}][quantity]`, item.quantity);
      });

      formData.append(
        "sections",
        this.getSectionsToRender()
          .map((s) => s.section)
          .join(","),
      );
      formData.append("sections_url", window.location.pathname);
      config.body = formData;

      try {
        const addUrl =
          (window.theme && theme.routes && theme.routes.cart_add_url) ||
          "/cart/add";
        const response = await fetch(addUrl, config);
        const parsedState = await response.json();
        if (parsedState && parsedState.status) {
          if (typeof dispatchCustomEvent === "function") {
            dispatchCustomEvent("product:variant-error", {
              source: "bulk-sale-page",
              errors: parsedState.description,
              message: parsedState.message,
            });
          }
          this.handleActionError(
            parsedState.description || parsedState.message,
          );
          return;
        }
        this.renderCartSections(parsedState);
        if (typeof dispatchCustomEvent === "function") {
          dispatchCustomEvent("cart:item-added", {
            product: parsedState.hasOwnProperty("items")
              ? parsedState.items[0]
              : parsedState,
          });
        }
        // this.clearSelected();
      } catch (error) {
        console.error(error);
      } finally {
        this.setButtonLoading(this.atcBtn, false);
        this.updateTotals();
      }
    }
  }

  customElements.define("bulk-sale-page", BulkSalePage);
}
